import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { UserAuthProvider } from "./contexts/userAuthContext";
import { BrandingProvider } from "./contexts/brandingContext";
import { WhitelabelProvider } from "./contexts/whitelabelContext";
import { NotificationProvider } from "./contexts/notificationContext";
import PWAUpdateManager from "./components/System/PWAUpdateManager";
import InstallPrompt from "./components/System/InstallPrompt";
import GlobalErrorBoundary from "../../../shared/components/ErrorBoundary/GlobalErrorBoundary";
// Lazy load larger components for better performance
import { lazyLoad } from "@shared/utils/lazyLoad";

const Login = lazyLoad(() => import("./pages/Login"));
const Signup = lazyLoad(() => import("./pages/Signup"));
const AccessRestricted = lazyLoad(() => import("./pages/AccessRestricted"));
const ApprovalPending = lazyLoad(() => import("./pages/ApprovalPending"));
const Transfer = lazyLoad(() => import("./pages/Transfer"));
const PWARedirect = lazyLoad(() => import("./pages/PWARedirect"));
const ForgotPassword = lazyLoad(() => import("./pages/ForgotPassword"));
const Home = lazyLoad(() => import("./pages/Home"));
const TermsOfService = lazyLoad(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazyLoad(() => import("./pages/PrivacyPolicy"));
const CommunityGuidelines = lazyLoad(
  () => import("./pages/CommunityGuidelines"),
);
const VerifyEmailReminder = lazyLoad(
  () => import("./pages/VerifyEmailReminder"),
);
const ProtectedRoute = lazyLoad(() => import("./components/ProtectedRoute"));
const ProtectedTrusteeRoute = lazyLoad(
  () => import("./components/System/ProtectedTrusteeRoute"),
);
const NotFound = lazyLoad(() => import("./pages/NotFound"));
const AffiliationAccessControl = lazyLoad(
  () => import("./components/AffiliationAccessControl"),
);
const WhitelabelLayout = lazyLoad(
  () => import("./components/Layout/WhitelabelLayout"),
);
const AdminDashboard = lazyLoad(
  () => import("./components/Institutional/AdminDashboard"),
);
const TrusteeDashboard = lazyLoad(
  () => import("./components/Trustee/TrusteeDashboard"),
);
const CommunitiesRouteProvider = lazyLoad(() =>
  import("./contexts/communityContext").then(({ CommunitiesProvider }) => ({
    default: CommunitiesProvider,
  })),
);
const DirectMessagesRouteProvider = lazyLoad(() =>
  import("./contexts/directMessagesContext").then(
    ({ DirectMessagesProvider }) => ({ default: DirectMessagesProvider }),
  ),
);
const InstitutionalRouteProvider = lazyLoad(() =>
  import("./contexts/institutionalContext").then(
    ({ InstitutionalProvider }) => ({ default: InstitutionalProvider }),
  ),
);
const Communities = lazyLoad(() => import("./pages/Communities"));
const Directory = lazyLoad(() => import("./pages/Directory"));
const Galleries = lazyLoad(() => import("./pages/Galleries"));
const Events = lazyLoad(() => import("./pages/Events"));
const Profile = lazyLoad(() => import("./pages/Profile"));
const DirectMessages = lazyLoad(() => import("./pages/DirectMessages"));
const EditProfile = lazyLoad(() => import("./pages/EditProfile"));
const Support = lazyLoad(() => import("./pages/Support"));
const Pass = lazyLoad(() => import("./pages/Pass"));
import { isSubdomainAccess, getSubdomain } from "@/utils/subdomain";
import { useBranding } from "./contexts/brandingContext";
import {
  resolveOrgTemplate,
  defaultSurfacePath,
} from "@shared/config/orgTemplates";
import "./index.css";

// Loading component for subdomain detection"
const SubdomainLoader: React.FC = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6d8986] mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Error component for invalid subdomain
const SubdomainError: React.FC<{ subdomain: string | null }> = ({
  subdomain,
}) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center max-w-md mx-auto p-6">
      <div className="text-red-500 text-6xl mb-4">⚠️</div>
      <h1 className="font-display text-2xl font-normal text-gray-900 mb-2">
        Subdomain Required
      </h1>
      <p className="text-gray-600 mb-4">
        {subdomain
          ? `The subdomain "${subdomain}" is not configured or is inactive.`
          : "This application requires access through a configured subdomain."}
      </p>
      <p className="text-sm text-gray-500">
        Please contact your organization administrator for the correct access
        link.
      </p>
    </div>
  </div>
);

/**
 * The tenant's route tree, built from its institution-type template.
 *
 * Render isolation: surfaces outside the tenant's template are never
 * registered as routes and never mounted — their lazy chunks are never even
 * fetched, so the other template leaves no trace in the DOM, CSS, or network.
 */
const TemplatedApp: React.FC = () => {
  const { subdomainConfig, loading } = useBranding();

  // Route composition depends on the template, so wait for the tenant config
  // before building the router (it is cached after first load).
  if (loading) {
    return <SubdomainLoader />;
  }

  const template = resolveOrgTemplate(subdomainConfig);
  const isClub = template.id === "club";
  const homePath = defaultSurfacePath(template);

  return (
    <Router>
      <Routes>
        {/* Public routes - no layout */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/access-restricted" element={<AccessRestricted />} />
        <Route path="/approval-pending" element={<ApprovalPending />} />
        <Route
          path="/verify-email-reminder"
          element={<VerifyEmailReminder />}
        />
        <Route path="/transfer" element={<Transfer />} />
        <Route path="/pwa-redirect" element={<PWARedirect />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/community-guidelines" element={<CommunityGuidelines />} />

        {/* Protected routes with WhitelabelLayout */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AffiliationAccessControl>
                <InstitutionalRouteProvider>
                  <WhitelabelLayout />
                </InstitutionalRouteProvider>
              </AffiliationAccessControl>
            </ProtectedRoute>
          }
        >
          {/* All protected pages go here as child routes */}
          <Route path="home" element={<Home />} />
          <Route path="dashboard" element={<Navigate to="/home" replace />} />

          {/* Template surfaces — only the tenant's template registers routes */}
          {template.modules.communities && (
            <Route
              path="communities"
              element={
                <CommunitiesRouteProvider>
                  <Outlet />
                </CommunitiesRouteProvider>
              }
            >
              <Route index element={<Communities />} />
              <Route path=":communityId" element={<Communities />} />
              <Route
                path=":communityId/threads/:threadId"
                element={<Communities />}
              />
            </Route>
          )}
          {template.modules.communities && (
            <Route
              path="discussions"
              element={<Navigate to="/communities" replace />}
            />
          )}
          <Route path="events" element={<Events />} />
          <Route path="directory" element={<Directory />} />
          <Route
            path="members"
            element={<Navigate to="/directory" replace />}
          />
          {template.modules.galleries && (
            <Route path="galleries" element={<Galleries />} />
          )}
          {template.modules.memberPass && (
            <Route path="pass" element={<Pass />} />
          )}
          <Route path="profile/:userId" element={<Profile />} />
          <Route path="profile" element={<Profile />} />
          <Route
            path="messages"
            element={
              <DirectMessagesRouteProvider>
                <DirectMessages />
              </DirectMessagesRouteProvider>
            }
          />
          <Route path="settings" element={<EditProfile />} />

          {/* Admin Routes */}
          <Route path="admin" element={<AdminDashboard />} />
          <Route
            path="admin/analytics"
            element={<Navigate to="/trustee" replace />}
          />
          <Route
            path="admin/settings"
            element={<Navigate to="/admin" replace />}
          />

          {/* Trustee Routes */}
          <Route element={<ProtectedTrusteeRoute />}>
            <Route path="trustee" element={<TrusteeDashboard />} />
          </Route>

          {/* Utility Routes */}
          <Route path="support" element={<Support />} />

          {/* Legacy routes for compatibility */}
          <Route
            path="community"
            element={
              <Navigate to={isClub ? homePath : "/communities"} replace />
            }
          />
          <Route
            path="help"
            element={
              <Navigate to={isClub ? homePath : "/communities"} replace />
            }
          />

          {/* Default protected route — the template's first surface */}
          <Route index element={<Navigate to={homePath} replace />} />
        </Route>

        {/* Fallback routes */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      {/* PWA Update Manager - Only shows on mobile devices */}
      <PWAUpdateManager
        onUpdateAvailable={() => {
          // Update available - handled silently
        }}
        onUpdateInstalled={() => {
          // Update installed - handled silently
        }}
      />

      {/* PWA Install Prompt - Only shows on mobile devices */}
      <InstallPrompt />
    </Router>
  );
};

function App() {
  const [subdomainStatus, setSubdomainStatus] = useState<
    "loading" | "valid" | "invalid"
  >("loading");
  const [currentSubdomain, setCurrentSubdomain] = useState<string | null>(null);

  useEffect(() => {
    // Initialize subdomain detection immediately when app loads
    const initializeSubdomain = () => {
      try {
        // Initializing whitelabel app

        const subdomain = getSubdomain();
        setCurrentSubdomain(subdomain);

        // For whitelabel app, we ALWAYS require a subdomain
        if (!isSubdomainAccess() || !subdomain) {
          setSubdomainStatus("invalid");
          return;
        }

        // Subdomain detected and validated

        // Don't query database here - let BrandingProvider handle it with proper caching
        // Just validate that we have a subdomain, configuration check happens in BrandingProvider
        setSubdomainStatus("valid");
      } catch (error) {
        setSubdomainStatus("invalid");
      }
    };

    initializeSubdomain();
  }, []);

  // Show loading while detecting subdomain
  if (subdomainStatus === "loading") {
    return <SubdomainLoader />;
  }

  // Show error if subdomain is invalid or missing
  if (subdomainStatus === "invalid") {
    return <SubdomainError subdomain={currentSubdomain} />;
  }

  // Render the main app if subdomain is valid
  // Error boundary is inside providers to ensure contexts are always available
  return (
    <NotificationProvider>
      <UserAuthProvider>
        <BrandingProvider>
          <GlobalErrorBoundary>
            <WhitelabelProvider>
              <TemplatedApp />
            </WhitelabelProvider>
          </GlobalErrorBoundary>
        </BrandingProvider>
      </UserAuthProvider>
    </NotificationProvider>
  );
}

export default App;
